/*
 * Tarjeta de cuotas de Addi en la ficha de producto.
 *
 * El contenido lo pinta `<addi-product-widget>`, un web component que sirve Addi desde
 * su CDN. No se puede vendorizar en `assets/` como Swiper: el componente consulta la API
 * de Addi con el `ally-slug` del comercio para traer el plan de cuotas, y la versión la
 * controlan ellos. Es la única excepción a la regla de «una sola librería externa»
 * (AGENTS.md §9); está anotada en AGENTS.md §13.
 *
 * Este archivo hace tres cosas que el wrapper oficial de Addi para Shopify no hace:
 *
 *  1. Inyecta el widget dentro del contenedor del diseño, en vez de dejar que Addi lo
 *     cuelgue al lado del primer `.price` que encuentre.
 *  2. Le pasa los colores y tipografías del tema leyéndolos de los tokens, para no
 *     duplicar hex aquí (AGENTS.md §4).
 *  3. Actualiza el precio al cambiar de talla. El wrapper de Addi lee la variante del
 *     `?variant=` de la URL una sola vez en `DOMContentLoaded`, así que con un selector
 *     de variantes sin recarga se queda mostrando la cuota de la talla inicial.
 */

const ADDI_WIDGET_SRC = 'https://cdn.addi.com/product-details-widget/prod/v1/product-details-widget.esm.js';

/* Los que se copian al reemplazar el nodo. `class` no: lo maneja Addi (`hydrated`). */
const ADDI_WIDGET_ATTRIBUTES = ['ally-slug', 'country', 'custom-widget-styles', 'price'];

class CheoAddi extends HTMLElement {
  connectedCallback() {
    this.widget = this.querySelector('addi-product-widget');
    if (!this.widget) return;

    this.loadWidgetScript();
    this.applyThemeStyles();
    this.watchRender();
    this.subscribeToVariantChange();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.unsubscribeFromVariantChange?.();
  }

  /* Una sola vez por página, aunque la ficha se recargue en el editor de temas. */
  loadWidgetScript() {
    if (document.querySelector('script[data-cheo-addi-widget]')) return;

    const script = document.createElement('script');
    script.type = 'module';
    script.src = ADDI_WIDGET_SRC;
    script.setAttribute('data-cheo-addi-widget', '');
    document.head.appendChild(script);
  }

  /*
   * El widget vive en shadow DOM, así que ni los tokens ni el anillo de foco del tema
   * llegan adentro: Addi solo expone este JSON. Los valores se leen de las custom
   * properties para que sigan saliendo de `design/tokens.css`.
   */
  applyThemeStyles() {
    const styles = getComputedStyle(this);
    const token = (name) => styles.getPropertyValue(name).trim();

    this.widget.setAttribute(
      'custom-widget-styles',
      JSON.stringify({
        widget: {
          borderColor: 'transparent',
          borderRadius: '0',
          margin: '0',
          fontColor: token('--c-body'),
          fontFamily: token('--f-body'),
          fontSize: token('--fs-small'),
          badgeBackgroundColor: token('--c-surface'),
          infoBackgroundColor: token('--c-surface'),
        },
        modal: {
          backgroundColor: token('--c-white'),
          cardColor: token('--c-surface'),
          fontColor: token('--c-body'),
          fontFamily: token('--f-body'),
          priceColor: token('--c-black-800'),
          badgeBackgroundColor: token('--c-surface'),
          badgeFontColor: token('--c-body'),
          badgeBorderRadius: token('--r-sm'),
          buttonBackgroundColor: token('--c-black-800'),
          buttonBorderColor: token('--c-black-800'),
          buttonBorderRadius: token('--r-pill'),
          buttonFontColor: token('--c-white'),
        },
      })
    );
  }

  /*
   * El borde y el relleno de la tarjeta solo aparecen cuando Addi confirma que hay plan
   * de cuotas. Si el aliado no está en producción, o el precio queda fuera del rango que
   * financian, el widget no pinta nada y un recuadro vacío se vería roto.
   *
   * La clase se añade una sola vez y nunca se quita: al cambiar de talla la tarjeta ya
   * está montada y quitarla provocaría un salto de layout.
   */
  watchRender() {
    if (this.classList.contains('cheo-addi--ready')) return;

    if (!('ResizeObserver' in window)) {
      this.classList.add('cheo-addi--ready');
      return;
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      if (!entries.some((entry) => entry.contentRect.height > 0)) return;
      this.classList.add('cheo-addi--ready');
      this.resizeObserver.disconnect();
    });

    this.resizeObserver.observe(this.widget);
  }

  subscribeToVariantChange() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.unsubscribeFromVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event?.data?.sectionId !== this.dataset.sectionId) return;

      const variant = event.data.variant;
      if (!variant) return;

      this.updatePrice(variant.price / 100);
    });
  }

  /*
   * Addi resuelve el plan de cuotas al montar el componente, así que cambiar el atributo
   * `price` no dispara una consulta nueva: hay que reemplazar el nodo, igual que hace el
   * wrapper oficial. Solo cuando el precio cambia de verdad — casi todas las tallas del
   * mismo par valen lo mismo y no vale la pena una petición por chip.
   */
  updatePrice(price) {
    if (!this.widget || Number(this.widget.getAttribute('price')) === price) return;

    const replacement = document.createElement('addi-product-widget');
    ADDI_WIDGET_ATTRIBUTES.forEach((name) => {
      const value = this.widget.getAttribute(name);
      if (value !== null) replacement.setAttribute(name, value);
    });
    replacement.setAttribute('price', price);

    this.resizeObserver?.disconnect();
    this.widget.replaceWith(replacement);
    this.widget = replacement;
    this.watchRender();
  }
}

customElements.define('cheo-addi', CheoAddi);
